/**
 * TabStrip — the universal tab strip across the whole viewing pane.
 *
 * Generalises EditorTabs (which was editor-only file tabs) to the unified tab
 * model: a tab is ANY view — the board, an open `.arch` file, a doc, a process,
 * the types view. Reads the store's `tabs` / `activeTabId` slices; click to
 * activate, × / middle-click to close, drag to reorder (@dnd-kit). A file tab
 * shows a dirty dot. `leading` / `trailing` slots host the nav/details collapse
 * toggles at the two ends, so they no longer float inside the board.
 */
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Eye,
  FileCode,
  FileText,
  LayoutDashboard,
  Puzzle,
  Shapes,
  Workflow,
  X,
  type LucideIcon,
} from "lucide-react";
import { ProcessGlyph } from "./icons.js";
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { IconButton, Tooltip } from "@archlang/ui";
import { useWorkspace } from "../WorkspaceContext.js";
import { ContextMenu, type MenuItem } from "../nav/ContextMenu.js";
import {
  useActiveTabId,
  useDirtyPaths,
  useTabs,
  type Tab,
  type WorkspaceStore,
} from "../WorkspaceStore.js";
import { OVERVIEW_TAB_ID } from "../tabs.js";
import { usePanelKindResolver } from "../plugins/PluginRegistryContext.js";
import type { PanelKind } from "../plugins/types.js";
import { displayLabel } from "../../displayLabel.js";
import { bareViewLabel } from "../../views/viewRepr.js";
import { useTranslation } from "../../i18n";

/** The `t` translator returned by {@link useTranslation}, threaded into the
 *  standalone `tabLabel` helper (which can't call the hook itself). */
type Translator = ReturnType<typeof useTranslation>["t"];

export interface TabStripProps {
  readonly className?: string;
  /** Rendered at the left end of the strip (e.g. the navigator toggle). */
  readonly leading?: ReactNode;
  /** Rendered at the right end of the strip (e.g. the details toggle). */
  readonly trailing?: ReactNode;
  /** Resolve a board-tab's rooted entity id to a human label (model-driven). */
  readonly entityLabel?: (id: string) => string | undefined;
}

const cx = (...a: (string | false | null | undefined)[]): string =>
  a.filter(Boolean).join(" ");

function basename(path: string): string {
  const i = path.lastIndexOf("/");
  return i >= 0 ? path.slice(i + 1) : path;
}

// Built-in kinds only; custom tabs render their PanelKind icon (a ReactNode) at
// the call site, so they never index this LucideIcon record.
const KIND_ICON: Record<Exclude<Tab["kind"], "custom">, LucideIcon> = {
  board: LayoutDashboard,
  file: FileCode,
  doc: FileText,
  // process tabs render the universal ProcessGlyph at the call site; this
  // placeholder only satisfies the Record<kind, LucideIcon> type.
  process: Workflow,
  types: Shapes,
  view: Eye,
  module: Box,
};

/** Display label + full title for a tab. */
function tabLabel(
  tab: Tab,
  t: Translator,
  entityLabel?: (id: string) => string | undefined,
): { label: string; title: string } {
  switch (tab.kind) {
    case "file": {
      const name = basename(tab.state.path);
      return { label: name, title: tab.state.path };
    }
    case "doc": {
      const name = basename(tab.state.path).replace(/\.(md|markdown)$/i, "");
      return { label: name, title: t("docTitle", { path: tab.state.path }) };
    }
    case "board": {
      // Name the board after the space it is currently focused in: the deepest
      // focal-path segment that resolves to a model name (root → "Overview").
      // Focal segments carry a kind suffix ("be002::space") — strip it first.
      const fp = tab.state.viewport?.focalPath ?? [];
      for (let i = fp.length - 1; i >= 0; i--) {
        const name = entityLabel?.(fp[i].split("::")[0]);
        if (name) {
          const d = displayLabel(name);
          return { label: d, title: t("boardTitle", { name: d }) };
        }
      }
      // No focal space yet — a freshly-rooted tab shows its entity, else home.
      if (tab.state.rootId) {
        const d = displayLabel(entityLabel?.(tab.state.rootId) ?? tab.state.rootId);
        return { label: d, title: t("boardTitle", { name: d }) };
      }
      return { label: t("overview"), title: t("workspaceOverview") };
    }
    case "process": {
      const lbl = tab.state.processId
        ? displayLabel(entityLabel?.(tab.state.processId) ?? tab.state.processId)
        : t("processes");
      return { label: lbl, title: t("processTitle", { name: lbl }) };
    }
    case "types":
      return { label: t("types"), title: t("types") };
    case "view":
      // The addressing id may carry a `Space::` term (v0.11) — display bare.
      return {
        label: displayLabel(bareViewLabel(tab.state.viewName)),
        title: t("viewTitle", { name: tab.state.viewName }),
      };
    case "module": {
      const lbl = displayLabel(
        tab.state.moduleName ?? entityLabel?.(tab.state.moduleId) ?? tab.state.moduleId,
      );
      return { label: lbl, title: t("moduleTitle", { name: lbl }) };
    }
    case "custom":
      // Fallback when the PanelKind isn't resolvable here; TabView overrides
      // with `panelKind.title(spec)` when a plugin owns this kind.
      return { label: tab.state.panelKind, title: tab.state.panelKind };
  }
}

/** Custom-tab label via its resolved PanelKind (a hook-fed override for
 *  `tabLabel`'s fallback). A throwing `title` degrades to the fallback. */
function customTabLabel(
  tab: Extract<Tab, { kind: "custom" }>,
  pk: PanelKind | undefined,
): { label: string; title: string } {
  if (!pk) return { label: tab.state.panelKind, title: tab.state.panelKind };
  try {
    const label = pk.title({
      kind: "custom",
      pluginId: tab.state.pluginId,
      panelKind: tab.state.panelKind,
      state: tab.state.state,
    });
    return { label, title: label };
  } catch {
    return { label: tab.state.panelKind, title: tab.state.panelKind };
  }
}

export function TabStrip(props: TabStripProps): ReactElement {
  const { t } = useTranslation("tabs");
  const store = useWorkspace();
  const tabs = useTabs(store);
  const activeId = useActiveTabId(store);
  const dirty = useDirtyPaths(store);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Right-click menu (anchored at the pointer). Built here so its actions reach
  // the store and one menu serves the whole strip.
  const [menu, setMenu] = useState<{ tab: Tab; x: number; y: number } | null>(null);
  const menuItems: readonly MenuItem[] = menu
    ? [
        // Duplicate makes a divergent-camera copy — only board tabs carry a
        // camera worth cloning; other kinds are deterministic singletons.
        ...(menu.tab.kind === "board"
          ? [{ label: t("duplicate"), onClick: () => store.duplicateTab(menu.tab.id) }]
          : []),
        ...(menu.tab.id !== OVERVIEW_TAB_ID
          ? [{ label: t("close"), onClick: () => store.closeTab(menu.tab.id) }]
          : []),
        ...(tabs.length > 1
          ? [{ label: t("closeOthers"), onClick: () => store.closeOtherTabs(menu.tab.id) }]
          : []),
      ]
    : [];

  const list = (
    <div
      role="tablist"
      aria-label={t("openTabs")}
      data-testid="tab-strip"
      className="editor-tabs-scroll flex min-w-0 flex-1 items-stretch overflow-x-auto"
    >
      {tabs.map((tab) => (
        <SortableTab
          key={tab.id}
          store={store}
          tab={tab}
          active={tab.id === activeId}
          // Doc tabs edit their file too (DocumentsPane writes go through
          // store.edit) — the unsaved-changes cue covers both path-carrying kinds.
          dirty={(tab.kind === "file" || tab.kind === "doc") && dirty.has(tab.state.path)}
          entityLabel={props.entityLabel}
          onContextMenu={(t, e) => {
            e.preventDefault();
            setMenu({ tab: t, x: e.clientX, y: e.clientY });
          }}
        />
      ))}
    </div>
  );

  const onDragEnd = (e: DragEndEvent): void => {
    const { active: a, over } = e;
    if (over && a.id !== over.id) store.reorderTabs(String(a.id), String(over.id));
  };

  return (
    <div
      className={cx(
        "flex items-stretch border-b border-slate-800 bg-slate-900 font-mono text-xs",
        props.className,
      )}
    >
      {props.leading && (
        <div className="flex shrink-0 items-center border-r border-slate-800 px-1.5">
          {props.leading}
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={tabs.map((t) => t.id)}
          strategy={horizontalListSortingStrategy}
        >
          {list}
        </SortableContext>
      </DndContext>
      {props.trailing && (
        <div className="flex shrink-0 items-center border-l border-slate-800 px-1.5">
          {props.trailing}
        </div>
      )}
      {menu && menuItems.length > 0 && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={menuItems}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}

interface TabViewProps {
  readonly store: WorkspaceStore;
  readonly tab: Tab;
  readonly active: boolean;
  readonly dirty: boolean;
  readonly entityLabel?: (id: string) => string | undefined;
  readonly onContextMenu?: (tab: Tab, e: ReactMouseEvent) => void;
  readonly drag?: {
    readonly setNodeRef: (el: HTMLElement | null) => void;
    readonly style: CSSProperties;
    readonly attributes: Record<string, unknown>;
    readonly listeners: Record<string, unknown> | undefined;
    readonly isDragging: boolean;
  };
}

function TabView({ store, tab, active, dirty, entityLabel, onContextMenu, drag }: TabViewProps): ReactElement {
  const { t } = useTranslation("tabs");
  const resolvePanelKind = usePanelKindResolver();
  const customPk = tab.kind === "custom" ? resolvePanelKind(tab.state.panelKind) : undefined;
  const { label, title } =
    tab.kind === "custom" ? customTabLabel(tab, customPk) : tabLabel(tab, t, entityLabel);
  const Icon = tab.kind === "custom" ? undefined : KIND_ICON[tab.kind];
  // The Overview board is the pinned home view — non-closable in the strip
  // (the store enforces it too). Every other tab closes freely.
  const canClose = tab.id !== OVERVIEW_TAB_ID;
  // Autoscroll the active tab into view whenever it becomes active — so opening
  // or navigating to a file (go-to-definition, issue popover, nav double-click)
  // always reveals its tab even when the strip has overflowed. Merged with the
  // dnd-kit node ref so both the sortable and the scroll target see the element.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const setRef = (el: HTMLDivElement | null): void => {
    rootRef.current = el;
    drag?.setNodeRef(el);
  };
  useEffect(() => {
    const el = rootRef.current;
    // Guard: jsdom (tests) has no scrollIntoView.
    if (active && typeof el?.scrollIntoView === "function")
      el.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [active]);
  return (
    <Tooltip content={title}>
    <div
      ref={setRef}
      style={drag?.style}
      {...drag?.attributes}
      {...drag?.listeners}
      role="tab"
      aria-selected={active}
      aria-label={title}
      onContextMenu={onContextMenu ? (e) => onContextMenu(tab, e) : undefined}
      // Shift-click closes the tab (anywhere on it); a plain click activates.
      onClick={(e) => {
        if (canClose && e.shiftKey) {
          e.preventDefault();
          store.closeTab(tab.id);
          return;
        }
        store.activateTab(tab.id);
      }}
      onAuxClick={
        canClose
          ? (e) => {
              if (e.button === 1) {
                e.preventDefault();
                store.closeTab(tab.id);
              }
            }
          : undefined
      }
      className={cx(
        "group relative flex shrink-0 cursor-pointer select-none items-center gap-1.5 border-r border-slate-800 px-3 py-1.5 outline-none transition-colors",
        active ? "bg-slate-950 text-slate-100" : "text-slate-400 hover:bg-slate-800/50",
        drag?.isDragging && "z-10 opacity-60",
      )}
    >
      {active && (
        <span aria-hidden="true" className="absolute inset-x-0 top-0 h-0.5 bg-arch-400" />
      )}
      {tab.kind === "process" ? (
        <ProcessGlyph className="h-3.5 w-3.5 shrink-0 opacity-70" />
      ) : tab.kind === "custom" ? (
        <span aria-hidden="true" className="flex h-3.5 w-3.5 shrink-0 items-center justify-center opacity-70">
          {customPk?.icon ?? <Puzzle className="h-3.5 w-3.5" />}
        </span>
      ) : (
        Icon && <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0 opacity-70" />
      )}
      <span className={cx("truncate", tab.kind === "file" && tab.state.preview && "italic")}>
        {label}
      </span>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
        {dirty && (
          <span
            aria-label={t("unsavedChangesAria")}
            title={t("unsavedChanges")}
            className={cx("h-1.5 w-1.5 rounded-full bg-amber-400", canClose && "group-hover:hidden")}
          />
        )}
        {canClose && (
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={t("closeTab", { name: label })}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              store.closeTab(tab.id);
            }}
            className={cx(
              dirty ? "hidden group-hover:inline-flex" : "",
              active || dirty ? "" : "opacity-0 group-hover:opacity-100",
            )}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        )}
      </span>
    </div>
    </Tooltip>
  );
}

function SortableTab(props: Omit<TabViewProps, "drag">): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: props.tab.id });
  return (
    <TabView
      {...props}
      drag={{
        setNodeRef,
        style: { transform: CSS.Transform.toString(transform), transition },
        attributes: attributes as unknown as Record<string, unknown>,
        listeners: listeners as unknown as Record<string, unknown> | undefined,
        isDragging,
      }}
    />
  );
}
